import React, { useState, useCallback } from 'react';
import { Controller, Control } from 'react-hook-form';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Typography,
    FormGroup,
    FormLabel,
    FormHelperText,
    Card,
    CardMedia,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { useDropzone } from 'react-dropzone';
import Cropper from 'react-easy-crop';
import { Area } from 'react-easy-crop/types';

interface BannerValidation {
    maxWidth?: number;
    maxHeight?: number;
    minWidth?: number;
    minHeight?: number;
    maxSizeInMB?: number;
    aspectRatio?: number;
}

interface InputBannerProps {
    name: string;
    label: string;
    control: Control<any>;
    validation?: BannerValidation;
    helpText?: string;
    required?: boolean;
    viewOnly?: boolean;
    existingFile?: {
        url?: string;
        name?: string;
        size?: number;
        mimetype?: string;
    };
}

const createImage = (url: string): Promise<HTMLImageElement> =>
    new Promise((resolve, reject) => {
        const image = new Image();
        image.addEventListener('load', () => resolve(image));
        image.addEventListener('error', error => reject(error));
        image.setAttribute('crossOrigin', 'anonymous');
        image.src = url;
    });

const getCroppedImg = async (imageSrc: string, pixelCrop: Area): Promise<Blob> => {
    const image = await createImage(imageSrc);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) {
        throw new Error('Could not create canvas context');
    }

    canvas.width = pixelCrop.width;
    canvas.height = pixelCrop.height;

    ctx.drawImage(
        image,
        pixelCrop.x,
        pixelCrop.y,
        pixelCrop.width,
        pixelCrop.height,
        0,
        0,
        pixelCrop.width,
        pixelCrop.height
    );

    return new Promise(resolve => {
        canvas.toBlob(blob => {
            if (blob) resolve(blob);
        }, 'image/jpeg', 0.95);
    });
};

const PREVIEW_BANNER_HEIGHT = 200;

interface DropzoneUploadProps {
    onDrop: (file: File) => void;
    disabled?: boolean;
    aspectRatio?: number;
    label?: string;
}

const DropzoneUpload: React.FC<DropzoneUploadProps> = ({ onDrop, disabled, aspectRatio = 16 / 9, label }) => {
    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop: (acceptedFiles) => {
            if (acceptedFiles.length > 0) {
                onDrop(acceptedFiles[0]);
            }
        },
        accept: {
            'image/*': ['.png', '.jpg', '.jpeg', '.gif', '.webp']
        },
        multiple: false,
        disabled,
    });

    return (
        <Box
            {...getRootProps()}
            sx={(theme) => ({
                width: '100%',
                maxWidth: 600,
                height: PREVIEW_BANNER_HEIGHT,
                aspectRatio: `${aspectRatio}`,
                position: 'relative',
                border: isDragActive ? '2px dashed' : 'none',
                borderColor: isDragActive ? theme.palette.primary.main : 'transparent',
                borderRadius: '8px',
                backgroundColor: theme.palette.mode === 'dark' ? '#282C30' : '#F9F9F9',
                cursor: disabled ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s ease',
                opacity: disabled ? 0.5 : 1,
                '&:hover': disabled ? {} : {
                    backgroundColor: theme.palette.mode === 'dark' ? '#2f3439' : '#f0f0f0',
                },
            })}
        >
            <input {...getInputProps()} />
            <Box
                sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: 2,
                }}
            >
                <CloudUploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                <Typography
                    variant="body2"
                    color="text.secondary"
                    align="center"
                    sx={{
                        fontFamily: "'Plus Jakarta Sans', sans-serif",
                        fontSize: '14px',
                        fontWeight: 500,
                    }}
                >
                    {isDragActive ? 'Déposez la bannière ici...' : label || 'Glissez-déposez une bannière ici ou cliquez pour sélectionner'}
                </Typography>
            </Box>
        </Box>
    );
};

const InputBanner: React.FC<InputBannerProps> = ({
    name,
    label,
    control,
    validation,
    helpText,
    required = false,
    viewOnly = false,
    existingFile
}) => {
    const [cropOpen, setCropOpen] = useState(false);
    const [imageSrc, setImageSrc] = useState<string | null>(null);
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
    const [previewUrl, setPreviewUrl] = useState<string | null>(existingFile?.url || null);
    const [error, setError] = useState<string | null>(null);

    const onCropComplete = useCallback((_: Area, croppedAreaPixels: Area) => {
        setCroppedAreaPixels(croppedAreaPixels);
    }, []);

    const validateFile = (file: File): string | null => {
        if (!validation) return null;

        // Size validation
        if (validation.maxSizeInMB && file.size > validation.maxSizeInMB * 1024 * 1024) {
            return `La taille du fichier doit être inférieure à ${validation.maxSizeInMB}Mo`;
        }

        // Type validation
        if (!file.type.startsWith('image/')) {
            return 'Le fichier doit être une image';
        }

        return null;
    };

    const validateImageDimensions = (image: HTMLImageElement): string | null => {
        if (!validation) return null;

        if (validation.minWidth && image.width < validation.minWidth) {
            return `La largeur de l'image doit être d'au moins ${validation.minWidth}px`;
        }
        if (validation.maxWidth && image.width > validation.maxWidth) {
            return `La largeur de l'image doit être d'au plus ${validation.maxWidth}px`;
        }
        if (validation.minHeight && image.height < validation.minHeight) {
            return `La hauteur de l'image doit être d'au moins ${validation.minHeight}px`;
        }
        if (validation.maxHeight && image.height > validation.maxHeight) {
            return `La hauteur de l'image doit être d'au plus ${validation.maxHeight}px`;
        }

        return null;
    };

    const handleFileSelect = (file: File, onChange: (file: File | null) => void) => {
        const fileError = validateFile(file);
        if (fileError) {
            setError(fileError);
            return;
        }

        const reader = new FileReader();
        reader.onload = async () => {
            const imageUrl = reader.result as string;
            
            try {
                const img = await createImage(imageUrl);
                const dimensionError = validateImageDimensions(img);
                
                if (dimensionError) {
                    setError(dimensionError);
                    return;
                }

                setImageSrc(imageUrl);
                setCropOpen(true);
                setError(null);
            } catch (err) {
                setError('Failed to load image');
            }
        };
        reader.readAsDataURL(file);
    };

    const handleCropSave = async (onChange: (file: File | null) => void) => {
        if (!imageSrc || !croppedAreaPixels) return;

        try {
            const croppedBlob = await getCroppedImg(imageSrc, croppedAreaPixels);
            const croppedFile = new File([croppedBlob], 'banner.jpg', { type: 'image/jpeg' });
            
            const previewUrl = URL.createObjectURL(croppedBlob);
            setPreviewUrl(previewUrl);
            
            onChange(croppedFile);
            setCropOpen(false);
            setImageSrc(null);
            setError(null);
        } catch (err) {
            setError('Échec du recadrage de l\'image');
        }
    };

    const handleRemove = (onChange: (file: File | null) => void) => {
        onChange(null);
        setPreviewUrl(null);
        setError(null);
    };

    return (
        <Controller
            name={name}
            control={control}
            rules={{ required: required ? `${label} est requis` : false }}
            render={({ field: { onChange, value }, fieldState: { error: fieldError } }) => (
                <FormGroup>
                    <FormLabel required={required} error={!!fieldError}>{label}</FormLabel>

                    {helpText && (
                        <FormHelperText>{helpText}</FormHelperText>
                    )}

                    {previewUrl ? (
                        <Box>
                            <Card sx={{ mb: 2, width: '100%', maxWidth: 600, height: PREVIEW_BANNER_HEIGHT, aspectRatio: `${validation?.aspectRatio || 16 / 9}` }}>
                                <CardMedia
                                    component="img"
                                    height={PREVIEW_BANNER_HEIGHT}
                                    image={previewUrl}
                                    alt="Aperçu de la bannière"
                                    sx={{ objectFit: 'cover' }}
                                />
                            </Card>
                            {!viewOnly && (
                                <Button
                                    variant="text"
                                    color="error"
                                    size="small"
                                    onClick={() => handleRemove(onChange)}
                                >
                                    Supprimer
                                </Button>
                            )}
                        </Box>
                    ) : (
                        !viewOnly && (
                            <DropzoneUpload
                                onDrop={(file) => handleFileSelect(file, onChange)}
                                disabled={false}
                                aspectRatio={validation?.aspectRatio || 16 / 9}
                                label="Glissez-déposez une bannière ici ou cliquez pour sélectionner"
                            />
                        )
                    )}

                    {(error || fieldError) && (
                        <FormHelperText error>
                            {error || fieldError?.message}
                        </FormHelperText>
                    )}

                    <Dialog
                        open={cropOpen}
                        onClose={() => setCropOpen(false)}
                        maxWidth="md"
                        fullWidth
                    >
                        <DialogTitle>Recadrer la bannière</DialogTitle>
                        <DialogContent>
                            <Box sx={{ position: 'relative', width: '100%', height: 400 }}>
                                {imageSrc && (
                                    <Cropper
                                        image={imageSrc}
                                        crop={crop}
                                        zoom={zoom}
                                        aspect={validation?.aspectRatio || 16 / 9}
                                        onCropChange={setCrop}
                                        onCropComplete={onCropComplete}
                                        onZoomChange={setZoom}
                                    />
                                )}
                            </Box>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setCropOpen(false)} variant="text" color="secondary">Annuler</Button>
                            <Button
                                onClick={() => handleCropSave(onChange)}
                                variant="contained"
                            >
                                Enregistrer
                            </Button>
                        </DialogActions>
                    </Dialog>
                </FormGroup>
            )}
        />
    );
};

export default InputBanner;
